// ============================================================
// ApiKeysPage.jsx — API key management + full inline API docs
// Mirrors ReportsPage.jsx's full-screen page pattern (top bar,
// left nav, theme-token styling).
// Backend: GET/POST/DELETE /api/v1/auth/keys (folded into
// backend/api/v1/auth/[action].js to stay under Vercel Hobby's
// 12-serverless-functions-per-deployment cap — see TSTITNW-2 follow-up)
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { getTheme } from '../lib/theme';
import {
  ArrowLeft, Key, Plus, Trash2, Copy, Check, AlertTriangle,
  BookOpen, Terminal, Code, Sparkles, Globe, Github, GitBranch,
  Play, RotateCcw, FileUp, ChevronDown, ChevronUp, Loader, X,
} from 'lucide-react';
import { SERVICES } from '../config/services';
import { tokenStore } from '../services/authService';

const KEYS_BASE = `${SERVICES.auth.BASE_URL}/api/v1/auth/keys`;

async function keysFetch(path, options = {}) {
  const token = tokenStore.get();
  const res = await fetch(`${KEYS_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data;
}

// ─────────────────────────────────────────────────────────────
// CODE BLOCK — copyable, syntax-agnostic
// ─────────────────────────────────────────────────────────────
function CodeBlock({ children, T }) {
  const [copied, setCopied] = useState(false);
  const text = typeof children === 'string' ? children : String(children);
  const copy = () => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div style={{ position: 'relative', background: '#0a0f1e', border: '1px solid rgba(0,212,170,0.15)', borderRadius: 10, marginTop: 8, marginBottom: 8 }}>
      <button onClick={copy} style={{
        position: 'absolute', top: 8, right: 8, display: 'flex', alignItems: 'center', gap: 4,
        padding: '4px 9px', borderRadius: 6, border: 'none', cursor: 'pointer',
        background: copied ? 'rgba(74,222,128,0.15)' : 'rgba(255,255,255,0.06)',
        color: copied ? '#4ade80' : '#94a3b8', fontSize: 11, fontWeight: 600,
      }}>
        {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? 'Copied' : 'Copy'}
      </button>
      <pre style={{ margin: 0, padding: '14px 16px', overflowX: 'auto', fontSize: 12, lineHeight: 1.6, fontFamily: "'SF Mono', Consolas, monospace", color: '#d1d9f0' }}>
        <code>{text}</code>
      </pre>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DOC SECTION — collapsible
// ─────────────────────────────────────────────────────────────
function DocSection({ id, title, icon: Icon, method, defaultOpen, T, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const methodColors = {
    GET: '#3b82f6', POST: '#00d4aa', DELETE: '#f87171', PUT: '#fbbf24',
  };
  return (
    <div id={id} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px',
        background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
      }}>
        {Icon && <Icon size={16} style={{ color: T.accent, flexShrink: 0 }} />}
        {method && (
          <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 5, background: `${methodColors[method]}22`, color: methodColors[method], fontFamily: 'monospace' }}>
            {method}
          </span>
        )}
        <span style={{ flex: 1, color: T.text, fontSize: 14, fontWeight: 700 }}>{title}</span>
        {open ? <ChevronUp size={16} style={{ color: T.textMuted }} /> : <ChevronDown size={16} style={{ color: T.textMuted }} />}
      </button>
      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function P({ children, T }) {
  return <p style={{ color: T.textSub, fontSize: 13, lineHeight: 1.7, marginBottom: 8 }}>{children}</p>;
}

function Field({ name, type, required, desc, T }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
      <code style={{ color: T.accent, fontSize: 12, fontFamily: 'monospace', minWidth: 130 }}>{name}</code>
      <span style={{ color: T.textMuted, fontSize: 11, minWidth: 60 }}>{type}</span>
      <span style={{ color: required ? '#f87171' : T.textMuted, fontSize: 11, minWidth: 70, fontWeight: required ? 700 : 400 }}>{required ? 'required' : 'optional'}</span>
      <span style={{ color: T.textSub, fontSize: 12, flex: 1 }}>{desc}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export default function ApiKeysPage({ onBack, theme = 'light' }) {
  const T = getTheme(theme);
  const [section, setSection] = useState('keys'); // 'keys' | 'docs'
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [revealedKey, setRevealedKey] = useState(null); // { key, name } shown once
  const [copiedRaw, setCopiedRaw] = useState(false);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await keysFetch('');
      setKeys(data.keys || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const activeCount = keys.filter(k => !k.revoked_at).length;

  const handleCreate = async () => {
    if (activeCount >= 10) {
      alert('You can have at most 10 active API keys. Revoke an unused one first.');
      return;
    }
    setCreating(true);
    try {
      const data = await keysFetch('', { method: 'POST', body: { name: newKeyName || 'Default key' } });
      setRevealedKey({ key: data.key, name: data.name });
      setNewKeyName('');
      loadKeys();
    } catch (e) {
      alert(`❌ ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`Revoke API key "${name}"? Any integrations using it will stop working immediately.`)) return;
    try {
      await keysFetch(`?id=${id}`, { method: 'DELETE' });
      loadKeys();
    } catch (e) {
      alert(`❌ ${e.message}`);
    }
  };

  const copyRaw = () => {
    navigator.clipboard?.writeText(revealedKey.key).then(() => {
      setCopiedRaw(true);
      setTimeout(() => setCopiedRaw(false), 1500);
    });
  };

  const navItems = [
    { id: 'keys', label: 'API Keys', icon: Key },
    { id: 'docs', label: 'Documentation', icon: BookOpen },
  ];

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text, display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div style={{ background: T.sidebar, borderBottom: `1px solid ${T.border}`, padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 13 }}>
            <ArrowLeft size={15} /> Back
          </button>
          <div style={{ width: 1, height: 20, background: T.border }} />
          <span style={{ color: T.text, fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Key size={16} style={{ color: T.accent }} /> API Keys & Documentation
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1 }}>
        {/* Sidebar */}
        <div style={{ width: 180, background: T.sidebar, borderRight: `1px solid ${T.border}`, padding: '10px 8px', flexShrink: 0 }}>
          {navItems.map(item => {
            const Icon = item.icon;
            const on = section === item.id;
            return (
              <button key={item.id} onClick={() => setSection(item.id)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', marginBottom: 2, fontSize: 13, fontWeight: on ? 600 : 400,
                background: on ? T.accentDim : 'transparent',
                color: on ? T.accent : T.textSub,
                borderLeft: `2px solid ${on ? T.accent : 'transparent'}`,
              }}>
                <Icon size={14} />
                <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Main content */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', maxWidth: 900 }}>

          {/* ══ KEYS ══ */}
          {section === 'keys' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 4 }}>API Keys</h2>
                <p style={{ color: T.textMuted, fontSize: 13 }}>
                  Use API keys to call TestItNow programmatically — analyze sources, run tests, and manage bugs without the browser.
                  Active: <strong style={{ color: T.text }}>{activeCount}</strong> / 10
                </p>
              </div>

              {/* Revealed-once banner */}
              {revealedKey && (
                <div style={{ background: 'rgba(0,212,170,0.08)', border: `1px solid ${T.accent}`, borderRadius: 12, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <AlertTriangle size={15} style={{ color: T.accent }} />
                    <strong style={{ color: T.text, fontSize: 13 }}>Copy this key now — it won't be shown again</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <code style={{ flex: 1, background: '#0a0f1e', color: '#00d4aa', padding: '8px 12px', borderRadius: 8, fontSize: 13, fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                      {revealedKey.key}
                    </code>
                    <button onClick={copyRaw} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', background: copiedRaw ? 'rgba(74,222,128,0.15)' : T.accentDim, color: copiedRaw ? '#4ade80' : T.accent, fontSize: 12, fontWeight: 700 }}>
                      {copiedRaw ? <Check size={13} /> : <Copy size={13} />} {copiedRaw ? 'Copied' : 'Copy'}
                    </button>
                    <button onClick={() => setRevealedKey(null)} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer' }}><X size={16} /></button>
                  </div>
                </div>
              )}

              {/* Create form */}
              <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16, display: 'flex', gap: 8 }}>
                <input
                  value={newKeyName}
                  onChange={e => setNewKeyName(e.target.value)}
                  placeholder="Key name (e.g. CI pipeline, Zapier integration)"
                  style={{ flex: 1, background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13, padding: '9px 12px', outline: 'none', fontFamily: 'inherit' }}
                />
                <button onClick={handleCreate} disabled={creating || activeCount >= 10} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, border: 'none',
                  cursor: creating || activeCount >= 10 ? 'not-allowed' : 'pointer',
                  background: creating || activeCount >= 10 ? T.bgHover : `linear-gradient(135deg,${T.accent},${T.blue})`,
                  color: creating || activeCount >= 10 ? T.textMuted : '#fff', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
                }}>
                  {creating ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />} New Key
                </button>
              </div>

              {/* Key list */}
              {error && <div style={{ color: '#f87171', fontSize: 13 }}>⚠ {error}</div>}
              {loading ? (
                <div style={{ textAlign: 'center', padding: 32, color: T.textMuted, fontSize: 13 }}>Loading…</div>
              ) : keys.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
                  <Key size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
                  <p style={{ fontSize: 13 }}>No API keys yet. Create one above to get started.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {keys.map(k => (
                    <div key={k.api_key_id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                      background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10,
                      opacity: k.revoked_at ? 0.5 : 1,
                    }}>
                      <Key size={15} style={{ color: k.revoked_at ? T.textMuted : T.accent, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>{k.name}</div>
                        <div style={{ color: T.textMuted, fontSize: 11, fontFamily: 'monospace' }}>
                          {k.key_prefix}••••••••
                          {k.revoked_at ? ` · revoked ${new Date(k.revoked_at).toLocaleDateString()}` : ''}
                          {k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleDateString()}` : ' · never used'}
                        </div>
                      </div>
                      <span style={{ fontSize: 11, color: T.textMuted }}>
                        created {new Date(k.created_at).toLocaleDateString()}
                      </span>
                      {!k.revoked_at && (
                        <button onClick={() => handleRevoke(k.api_key_id, k.name)} style={{
                          display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 7,
                          border: 'none', background: 'rgba(248,113,113,0.12)', color: '#f87171', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}>
                          <Trash2 size={12} /> Revoke
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══ DOCS ══ */}
          {section === 'docs' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 4 }}>API Documentation</h2>
                <p style={{ color: T.textMuted, fontSize: 13 }}>
                  Every endpoint below accepts the same requests the TestItNow UI sends — website analysis, GitHub/GitLab repo
                  analysis, source-file/spec upload, running tests, and managing bugs. Authenticate with an API key instead of
                  a login session and everything else works the same.
                </p>
              </div>

              {/* AUTH */}
              <DocSection id="doc-auth" title="Authentication" icon={Key} T={T} defaultOpen>
                <P T={T}>
                  Send your API key as a Bearer token on every request. Unlike the browser session token, API keys don't
                  expire — they stay valid until you revoke them from the Keys tab.
                </P>
                <CodeBlock T={T}>{`Authorization: Bearer tin_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</CodeBlock>
                <P T={T}>
                  All endpoints live under <code style={{ color: T.accent }}>{SERVICES.auth.BASE_URL}/api/v1/</code>.
                  Requests without a valid key or session token get <code>401 Unauthorized</code>.
                </P>
              </DocSection>

              {/* ANALYZE (website / repo) */}
              <DocSection id="doc-analyze" title="Analyze a Website or Repository" icon={Sparkles} method="POST" T={T}>
                <P T={T}><code style={{ color: T.accent }}>POST /api/v1/engine/analyze</code></P>
                <P T={T}>
                  Crawls a live website (or clones a GitHub/GitLab repo, via <code>sources</code> below) and generates test
                  cases synchronously. Because generation is deterministic (no AI calls), this responds in one request —
                  no polling required.
                </P>
                <Field name="url" type="string" required desc="Full https:// URL of the site to crawl" T={T} />
                <Field name="projectId" type="uuid" required desc="Target project (create via POST /api/v1/projects)" T={T} />
                <Field name="authCredentials" type="object" desc='{ username, password } for pages behind a login wall' T={T} />
                <Field name="maxPages" type="number" desc="Pages to crawl, capped at 20 (default 15)" T={T} />
                <CodeBlock T={T}>{`curl -X POST ${SERVICES.auth.BASE_URL}/api/v1/engine/analyze \\
  -H "Authorization: Bearer tin_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://yourapp.com",
    "projectId": "b2f1...-uuid",
    "maxPages": 15
  }'`}</CodeBlock>
                <P T={T}><strong style={{ color: T.text }}>Response</strong> — test cases already persisted to the project:</P>
                <CodeBlock T={T}>{`{
  "success": true,
  "message": "Crawled 8 pages, generated 142 test cases",
  "generated": { "ui": [...], "api": [...], "unit": [...], ... },
  "meta": {
    "url": "https://yourapp.com",
    "domain": "yourapp.com",
    "pagesAnalyzed": 8,
    "totalGenerated": 142,
    "savedToDB": 142,
    "breakdown": { "ui": 40, "api": 25, "unit": 30, "security": 12, "performance": 10, "database": 25 }
  }
}`}</CodeBlock>
              </DocSection>

              {/* ANALYZE-UPLOAD */}
              <DocSection id="doc-upload" title="Analyze Uploaded Source Files / Specs" icon={FileUp} method="POST" T={T}>
                <P T={T}><code style={{ color: T.accent }}>POST /api/v1/engine/analyze-upload</code></P>
                <P T={T}>
                  For code you can't crawl over HTTP — source files, SQL schemas, or plain-text/markdown project
                  specifications. Send raw file contents and the backend generates and persists test cases in one call
                  (no browser required).
                </P>
                <Field name="projectId" type="uuid" required desc="Target project" T={T} />
                <Field name="files" type="array" required desc='[{ filename, content }] — .js/.ts/.jsx/.tsx, .sql, .md, .txt supported' T={T} />
                <Field name="label" type="string" desc={'Friendly name for this upload batch (default: "Uploaded files")'} T={T} />
                <CodeBlock T={T}>{`curl -X POST ${SERVICES.auth.BASE_URL}/api/v1/engine/analyze-upload \\
  -H "Authorization: Bearer tin_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "projectId": "b2f1...-uuid",
    "label": "checkout-service",
    "files": [
      { "filename": "checkout.js", "content": "function processPayment(...) { ... }" },
      { "filename": "spec.md",     "content": "# Requirements\\n- Users can apply a promo code\\n- Refunds must complete within 5 days" }
    ]
  }'`}</CodeBlock>
                <P T={T}>
                  Function/component names and Express-style routes (<code>router.get('/path', ...)</code>) are extracted from
                  code files into unit and API tests. Bullet points and headings in <code>.md</code>/<code>.txt</code> files
                  become requirement-verification tests. <code>.sql</code> files get CRUD tests per <code>CREATE TABLE</code>.
                </P>
                <CodeBlock T={T}>{`{
  "success": true,
  "message": "Generated 34 test cases from \\"checkout-service\\"",
  "generated": { "unit": [...], "api": [...], "database": [] },
  "meta": { "label": "checkout-service", "filesAnalyzed": 2, "totalGenerated": 34, "savedToDB": 34, "breakdown": {...} }
}`}</CodeBlock>
              </DocSection>

              {/* SOURCES (GitHub/GitLab repo analysis) */}
              <DocSection id="doc-sources" title="Analyze a GitHub / GitLab Repository" icon={Github} method="POST" T={T}>
                <P T={T}><code style={{ color: T.accent }}>POST /api/v1/sources/analyze-repo</code></P>
                <P T={T}>
                  Clones a public or token-accessible repository and runs the same generator against its source files —
                  the API equivalent of the GitHub/GitLab tabs in the Analyze screen.
                </P>
                <Field name="projectId" type="uuid" required desc="Target project" T={T} />
                <Field name="provider" type="string" required desc='"github" or "gitlab"' T={T} />
                <Field name="repoOwner" type="string" required desc="Repo owner / namespace" T={T} />
                <Field name="repoName" type="string" required desc="Repository name" T={T} />
                <Field name="branch" type="string" desc="Defaults to main" T={T} />
                <Field name="accessToken" type="string" desc="PAT for private repos" T={T} />
                <CodeBlock T={T}>{`curl -X POST ${SERVICES.auth.BASE_URL}/api/v1/sources/analyze-repo \\
  -H "Authorization: Bearer tin_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "projectId": "b2f1...-uuid",
    "provider": "github",
    "repoOwner": "your-org",
    "repoName": "your-repo",
    "branch": "main"
  }'`}</CodeBlock>
              </DocSection>

              {/* RUN */}
              <DocSection id="doc-run" title="Run Test Cases" icon={Play} method="POST" T={T}>
                <P T={T}><code style={{ color: T.accent }}>POST /api/v1/engine/run</code></P>
                <P T={T}>Executes a batch of test cases (from any of the analyze endpoints above) and records results with screenshots.</P>
                <Field name="tests" type="array" required desc="Test case objects, each including dbId from analyze" T={T} />
                <Field name="environment" type="string" desc='Default "production"' T={T} />
                <Field name="browser" type="string" desc='Default "chrome"' T={T} />
                <Field name="os" type="string" desc='Default "linux"' T={T} />
                <CodeBlock T={T}>{`curl -X POST ${SERVICES.auth.BASE_URL}/api/v1/engine/run \\
  -H "Authorization: Bearer tin_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{ "tests": [ { "dbId": "...", "name": "Login form validates email", "type": "ui" } ] }'`}</CodeBlock>
                <P T={T}>Free-plan keys consume one test run from the monthly quota per call, same as the UI.</P>
              </DocSection>

              {/* RETEST */}
              <DocSection id="doc-retest" title="Retest a Single Case" icon={RotateCcw} method="POST" T={T}>
                <P T={T}><code style={{ color: T.accent }}>POST /api/v1/engine/retest</code></P>
                <Field name="test" type="object" required desc="The test object to re-run, including dbId" T={T} />
                <CodeBlock T={T}>{`curl -X POST ${SERVICES.auth.BASE_URL}/api/v1/engine/retest \\
  -H "Authorization: Bearer tin_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{ "test": { "dbId": "...", "name": "Login form validates email" } }'`}</CodeBlock>
              </DocSection>

              {/* PROJECTS */}
              <DocSection id="doc-projects" title="Projects" icon={Code} method="GET" T={T}>
                <P T={T}><code style={{ color: T.accent }}>GET / POST /api/v1/projects</code></P>
                <P T={T}>List or create the projects that <code>projectId</code> above refers to.</P>
                <CodeBlock T={T}>{`curl ${SERVICES.auth.BASE_URL}/api/v1/projects \\
  -H "Authorization: Bearer tin_xxx"`}</CodeBlock>
              </DocSection>

              {/* BUGS */}
              <DocSection id="doc-bugs" title="Bug Reports" icon={AlertTriangle} method="POST" T={T}>
                <P T={T}><code style={{ color: T.accent }}>GET / POST /api/v1/bugs/[action]</code></P>
                <P T={T}>Create or list bugs filed from failed test results — same data your Jira/Azure export uses.</P>
              </DocSection>

              {/* ERRORS */}
              <DocSection id="doc-errors" title="Errors" icon={AlertTriangle} T={T}>
                <P T={T}>All errors return a JSON body with an <code>error</code> string and an appropriate HTTP status:</P>
                <CodeBlock T={T}>{`401  Invalid or revoked API key
400  Missing required field (see error message for which one)
403  Free-plan test run quota exhausted
404  Project / test / key not found
500  Internal server error (detail included in non-production)`}</CodeBlock>
              </DocSection>

              <div style={{ textAlign: 'center', padding: '12px 0', color: T.textMuted, fontSize: 12 }}>
                Questions about the API? Reach out from your account settings — we read every message.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
